/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Apr 05 2024
 **/

import { useRef, useEffect, useState, WheelEvent } from "react";
import { TransformComponent, TransformWrapper, ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { debounce } from "lodash-es";

import { EventHandler } from "@rfice/eventhandler";
import { _T } from "@rfice/language";

import { useCtrlPressed } from "@/frameworks/core/hooks";
import { Environments, Utility } from "@/frameworks/libraries/utilities";
import useDocumentViewerStore from "../../stores/useDocumentViewerStore";
import useDocumentViewerTransform from "../hooks/useDocumentViewerTransform";
import VideoPlayer from "./VideoPlayer";
import { LoadingFile } from "../../emotion";
import { VideoViewerWrap, VideoControlsWrapper } from "./emotion";
import { MAX_SCALE } from "../constant";

export default function VideoViewer({
	fileID,
	mimeType,
	minimize
}: {
	fileID: string;
	mimeType: string;
	minimize?: boolean;
}) {
	const scale = useDocumentViewerStore((state) => state.scale);
	const prevScale = useDocumentViewerStore((state) => state.prevScale);
	const minScale = useDocumentViewerStore((state) => state.minScale);

	const [videoLoaded, setVideoLoaded] = useState(false);

	const transformWrapperRef = useRef<ReactZoomPanPinchRef | null>(null);
	const videoControlsRef = useRef<HTMLDivElement>(null);
	const isWheelZooming = useRef(false);

	const { handleWheel, updateViewerToCenter, updatePanning } = useDocumentViewerTransform(transformWrapperRef);
	const { isCtrlPressed } = useCtrlPressed();

	const partnerURL = Utility.URLNormalize(Environments.env<string>("URL_VIDEO"));

	/* Initialize */
	useEffect(() => {
		if (videoLoaded) {
			const controlbar = document.querySelector(".vjs-control-bar");
			if (controlbar) videoControlsRef.current?.appendChild(controlbar);
		}
	}, [videoLoaded]);

	const onMetadataLoaded = ({ width, height }: { width: number; height: number }) => {
		EventHandler.fire("$Viewer::FileLoaded$", { width: width, height: height }, "transform-content");
		updateViewerToCenter(scale, 0);
	};

	/* Resize */
	useEffect(() => {
		// resize가 될 때 문서를 화면의 센터로 맞춘다
		const handleResize = debounce(() => updateViewerToCenter(), 100);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [scale]);

	useEffect(() => {
		videoLoaded && updateViewerToCenter(scale, 0);
	}, [minimize]);

	/* Update Transform */
	useEffect(() => {
		if (isWheelZooming.current) return;
		const factor = Math.log(scale / prevScale);
		if (scale > prevScale) transformWrapperRef.current?.zoomIn(factor, 0);
		else transformWrapperRef.current?.zoomOut(-factor, 0);
	}, [scale]);

	/* Scroll */
	const handleScroll = (e: WheelEvent) => {
		if (isCtrlPressed || scale === minScale) return;
		updatePanning(e.deltaY);
	};

	const handleZoomStateChanged = (e: ReactZoomPanPinchRef, isStart: boolean) => {
		// zoom out인데 minScale일 경우 center
		if (!isStart && e.state.scale === minScale) updateViewerToCenter(e.state.scale);
		isWheelZooming.current = isStart;
	};

	return (
		<VideoViewerWrap onWheel={handleScroll}>
			{!videoLoaded && (
				<LoadingFile typoSize="Head5_B" typoColor="neutral_00_100">
					{_T("로딩중")}
				</LoadingFile>
			)}
			<TransformWrapper
				ref={transformWrapperRef}
				minScale={minScale}
				maxScale={MAX_SCALE}
				wheel={{ step: 1, disabled: !isCtrlPressed }}
				pinch={{ disabled: false, step: 0.05 }}
				panning={{ disabled: false }}
				doubleClick={{ disabled: true }}
				limitToBounds={true}
				centerOnInit={true}
				onWheel={handleWheel}
				onWheelStart={(e) => handleZoomStateChanged(e, true)}
				onWheelStop={(e) => handleZoomStateChanged(e, false)}
				centerZoomedOut={isWheelZooming.current} // wheel zooming일때 center 되는 것을 방지
				zoomAnimation={{ disabled: true }} // ctrl + wheel시 라이브러리 자체에서 처리되는 규칙을 무시하기 위함
			>
				<TransformComponent
					contentStyle={{
						visibility: "visible",
						position: "absolute"
					}}
				>
					<div id="transform-content">
						<VideoPlayer
							onLoad={() => setVideoLoaded(true)}
							options={{
								sources: [
									{
										src: `${partnerURL}/filebox/files/${fileID}?playsinline=1`,
										type: mimeType
									}
								],
								autoplay: true
							}}
							onMetadataLoaded={onMetadataLoaded}
						/>
					</div>
				</TransformComponent>
			</TransformWrapper>
			<VideoControlsWrapper ref={videoControlsRef} className="video-js vjs-controls-enabled vjs-has-started" />
		</VideoViewerWrap>
	);
}
